import { SoundButton } from "../SoundButton/SoundButton";
import { useId, useRef } from "react";
import { useTranslation } from "react-i18next";
import {
    REWARD_COLLECTION_TIMING,
    type RewardCollectionPresentation,
    type RewardCollectionReason,
} from "../../../logic/helpers/rewardCollection";
import { useRewardCollectionTimeline } from "../../../logic/hooks/useRewardCollectionTimeline";
import { SimpleDialog } from "../SimpleDialog/SimpleDialog";
import { RewardCollectionItem } from "./components/RewardCollectionItem/RewardCollectionItem";
import "./RewardCollectionDialog.scss";

interface RewardCollectionDialogProps {
    /** null (or an empty reward list) renders nothing and invokes no callback. */
    presentation: RewardCollectionPresentation | null;
    /** Visible milliseconds since opening; null keeps it open until interaction. */
    autoCollectAfterMs?: number | null;
    /** Collection was requested, before the exit animation; presentation only. */
    onCollect?: (
        presentationId: string,
        reason: RewardCollectionReason,
    ) => void;
    /** Presentation has finished; this callback must not grant the reward again. */
    onComplete: (
        presentationId: string,
        reason: RewardCollectionReason,
    ) => void;
}

export function RewardCollectionDialog(props: RewardCollectionDialogProps) {
    if (props.presentation === null || props.presentation.rewards.length === 0)
        return null;
    return (
        <RewardCollectionSession
            key={props.presentation.id}
            presentation={props.presentation}
            autoCollectAfterMs={props.autoCollectAfterMs}
            onCollect={props.onCollect}
            onComplete={props.onComplete}
        />
    );
}

interface RewardCollectionSessionProps extends Omit<
    RewardCollectionDialogProps,
    "presentation"
> {
    presentation: RewardCollectionPresentation;
}

function RewardCollectionSession({
    presentation,
    autoCollectAfterMs = REWARD_COLLECTION_TIMING.autoCollectMs,
    onCollect,
    onComplete,
}: RewardCollectionSessionProps) {
    const { t } = useTranslation();
    const listId = useId();
    const lightFilterId = useId();
    const collectButton = useRef<HTMLButtonElement>(null);
    const { bodyRef, phase, collect } = useRewardCollectionTimeline({
        autoCollectAfterMs,
        rewardCount: presentation.rewards.length,
        onCollect: (reason) => onCollect?.(presentation.id, reason),
        onComplete: (reason) => onComplete(presentation.id, reason),
    });
    const count = presentation.rewards.length;
    const layout = count <= 3 ? "row" : count === 4 ? "quad" : "many";

    return (
        <SimpleDialog
            className="reward-collection-dialog"
            isOpen={phase !== "closed"}
            initialFocusRef={collectButton}
            onClose={() => collect("dismiss")}
            closeOnBackdropClick={false}
            aria-label={t("rewardCollection.title")}
            data-phase={phase}
            data-layout={layout}
        >
            <div className="reward-collection-dialog__body" ref={bodyRef}>
                <svg
                    className="reward-collection-dialog__filters"
                    aria-hidden="true"
                    width="0"
                    height="0"
                >
                    <defs>
                        <filter
                            id={lightFilterId}
                            colorInterpolationFilters="sRGB"
                        >
                            {/* Captured Sprites/Desaturate shader: average RGB + .15, then vertex tint. */}
                            <feColorMatrix
                                type="matrix"
                                values=".333333 .333333 .333333 0 .15 .321607 .321607 .321607 0 .144723 .240566 .240566 .240566 0 .108255 0 0 0 1 0"
                            />
                        </filter>
                    </defs>
                </svg>
                <ul className="reward-collection-dialog__items" id={listId}>
                    {presentation.rewards.map((reward) => (
                        <RewardCollectionItem
                            key={reward.id}
                            reward={reward}
                            lightFilterId={lightFilterId}
                        />
                    ))}
                </ul>
                <div
                    className="reward-collection-dialog__action"
                    aria-hidden="true"
                >
                    <p className="reward-collection-dialog__prompt">
                        {t("rewardCollection.tapToCollect")}
                    </p>
                    <div className="reward-collection-dialog__countdown">
                        <span className="reward-collection-dialog__countdown-fill" />
                    </div>
                </div>
                <SoundButton
                    ref={collectButton}
                    type="button"
                    className="reward-collection-dialog__collect"
                    aria-label={t("rewardCollection.tapToCollect")}
                    aria-describedby={listId}
                    aria-disabled={phase === "exiting" || phase === "closed"}
                    onClick={() => collect("tap")}
                />
            </div>
        </SimpleDialog>
    );
}
